import cubejs, { type CubeApi, type Query } from '@cubejs-client/core'

import { apiFetch } from '@/lib/api'
import { LiveTransport } from '@/lib/cube-ws-transport'
import { useRealtimeStore } from '@/stores/realtime-store'

/** Backend `POST /auth/cube-token` yanıtı (bkz. docs/CUBE-MIGRATION-PLAN.md G2.2). */
interface CubeTokenResponse {
  token: string
  expires_in: number
}

/**
 * Cube token bellekte tutulur, `localStorage`'a asla yazılmaz (AGENTS.md §4 —
 * uygulama access token'ı için geçerli kural Cube token'ı için de geçerli).
 * Süresi dolmadan 60 sn önce `/auth/cube-token` ile yenilenir.
 */
const REFRESH_MARGIN_MS = 60_000

let cachedToken: string | null = null
let cachedExpiresAt = 0
let pendingFetch: Promise<string> | null = null

async function fetchCubeToken(): Promise<string> {
  const response = await apiFetch<CubeTokenResponse>('/auth/cube-token', { method: 'POST' })
  cachedToken = response.token
  cachedExpiresAt = Date.now() + response.expires_in * 1000
  return cachedToken
}

async function getCubeToken(): Promise<string> {
  if (cachedToken && Date.now() < cachedExpiresAt - REFRESH_MARGIN_MS) {
    return cachedToken
  }
  pendingFetch ??= fetchCubeToken().finally(() => {
    pendingFetch = null
  })
  return pendingFetch
}

/** Tarayıcıdan aynı origin üzerinden nginx'in `/cube/` bloğuna gider (bkz. G0.4). */
export const cubeApi: CubeApi = cubejs(getCubeToken, {
  apiUrl: '/cube/cubejs-api/v1',
  // Cube REST API'si ölçü/boyut değerlerini varsayılan olarak string döner;
  // sayısal tipli üyeleri istemcide otomatik `number`'a çevirir.
  castNumerics: true,
})

/*
 * Canlı abonelik istemcisi (bkz. docs/CUBE-MIGRATION-PLAN.md Faz 6, K1/K3).
 * Yalnızca `subscribe` için kullanılır; ilk yükleme `cubeApi` (HTTP) ile yapılır,
 * böylece WS'nin düşmesi verinin düşmesi anlamına gelmez. İlk abonelikte kurulur:
 * login ekranında soket açılmaz.
 */
let liveTransport: LiveTransport | null = null
let liveApi: CubeApi | null = null
let rotationTimer: ReturnType<typeof setInterval> | undefined

/**
 * Cube token'ı WS'de yalnızca bağlantı açılırken doğrular (plan F4/F5). Soket açık
 * kaldıkça token'ı biz yeniler, değişince `authorization` atayarak yeniden
 * bağlanmaya zorlarız. `getCubeToken` ağa yalnızca süre dolmaya yakınken gider.
 * Yenileme başarısız olursa (oturum bitti) bir şey yapılmaz: süresi dolan token'la
 * gelen her abonelik sonucunu Cube'deki `queryRewrite` kapısı (K4) keser.
 */
const ROTATION_CHECK_MS = 30_000

async function rotateLiveToken(): Promise<void> {
  if (!liveTransport?.hasSocket) return
  try {
    const token = await getCubeToken()
    if (liveTransport.authorization !== token) liveTransport.authorization = token
  } catch {
    // bkz. yukarıdaki not — K4 kapısı yeterli.
  }
}

function liveUrl(): string {
  const scheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${scheme}//${window.location.host}/cube/ws`
}

export function getCubeLiveApi(): CubeApi {
  if (!liveApi) {
    const { setStatus, recordEvent } = useRealtimeStore.getState()
    const apiUrl = liveUrl()
    liveTransport = new LiveTransport(apiUrl, {
      onStatus: setStatus,
      onData: () => {
        recordEvent(new Date().toISOString())
      },
    })
    liveApi = cubejs(getCubeToken, { apiUrl, transport: liveTransport, castNumerics: true })
    rotationTimer = setInterval(() => void rotateLiveToken(), ROTATION_CHECK_MS)
  }
  return liveApi
}

/**
 * Çıkışta çağrılır: soket kapanır ve bellekteki Cube token'ı silinir — aynı
 * sekmede sonra giriş yapan kullanıcı öncekinin izinleriyle sorgu atmasın.
 */
export function closeCubeSession(): void {
  clearInterval(rotationTimer)
  void liveTransport?.close()
  liveTransport = null
  liveApi = null
  cachedToken = null
  cachedExpiresAt = 0
}

/** Sözleşme §1.4 invariantı: her Cube sorgusu bu saat diliminde çalışır. */
export const CUBE_TIMEZONE = 'Europe/Istanbul'

/**
 * Tüm Cube sorguları bu fonksiyondan geçer — `timezone` atlanamaz (plan §1.4/G3.2).
 * Sayfa kodu ham `Query` nesnesi yazmaz.
 */
export function buildCubeQuery(query: Query): Query {
  return { ...query, timezone: CUBE_TIMEZONE }
}
